import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Truck, FileCheck2, ShieldCheck, Boxes, ScanLine,
  Users, KeyRound, Settings, ScrollText, ClipboardList, Building2,
  Package, CalendarRange, LogOut, Bell, FileText, Trash2, Pencil,
  Wrench, UserPlus, AlertTriangle, ChevronDown, ChevronRight
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_supply-control-20/artifacts/rvxe15j7_1.png";

// Sidebar restructured (Feb-2026): each section is a collapsible group.
// `default` controls initial open/closed state per group.
const GROUPS = [
  {
    title: null,  // top-level (no heading)
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, module: "dashboard" },
    ],
  },
  {
    title: "OPERATIONS",
    defaultOpen: true,
    items: [
      { to: "/gate-entry", label: "Gate Entry", icon: Truck, module: "gate_entry" },
      { to: "/grn", label: "GRN", icon: FileCheck2, module: "grn" },
      { to: "/quality", label: "Quality", icon: ShieldCheck, module: "quality" },
      { to: "/stock", label: "Stock & FIFO", icon: Boxes, module: "stock" },
      { to: "/material-requests", label: "Material Requests", icon: ClipboardList, module: "outward" },
      { to: "/outward", label: "Outward / Scan", icon: ScanLine, module: "outward" },
      { to: "/adjustments", label: "Inventory Adjustments", icon: ScrollText, module: "inventory_adj" },
    ],
  },
  {
    title: "PROCUREMENT",
    defaultOpen: true,
    items: [
      { to: "/purchase-orders", label: "Purchase Orders", icon: FileText, module: "purchase_orders" },
      { to: "/challans", label: "Delivery Challans", icon: FileText, module: "challans" },
      { to: "/schedules", label: "Schedules", icon: CalendarRange, module: "schedules" },
      { to: "/supplier-schedules", label: "Supplier Schedules", icon: CalendarRange, module: "schedules" },
      { to: "/reports/abnormal", label: "Abnormal Report", icon: ScrollText, module: "purchase_orders" },
      { to: "/reports/ng-trend", label: "NG Trend (QA)", icon: ScrollText, module: "quality" },
      { to: "/fifo-overrides", label: "FIFO Overrides", icon: ScrollText, module: "outward" },
      { to: "/reports", label: "Reports Centre", icon: ScrollText, module: "purchase_orders" },
    ],
  },
  {
    title: "MASTER DATA",
    defaultOpen: false,
    items: [
      { to: "/suppliers", label: "Suppliers", icon: Building2, module: "suppliers" },
      { to: "/materials", label: "Materials", icon: Package, module: "materials" },
      { to: "/material-categories", label: "Material Categories", icon: Package, module: "materials" },
      { to: "/templates", label: "Inspection Templates", icon: ClipboardList, module: "templates" },
    ],
  },
  {
    title: "TRACKING",
    defaultOpen: false,
    items: [
      { to: "/assets", label: "Asset Register", icon: Wrench, module: "assets" },
      { to: "/visitors", label: "Visitor Management", icon: UserPlus, module: "visitors" },
    ],
  },
  {
    title: "ADMIN",
    defaultOpen: false,
    items: [
      { to: "/users", label: "Users", icon: Users, module: "users" },
      { to: "/roles", label: "Roles & Permissions", icon: KeyRound, module: "roles" },
      { to: "/settings", label: "Settings", icon: Settings, module: "settings" },
      { to: "/deleted-records", label: "Deleted Records", icon: Trash2, module: "users" },
      { to: "/approvals", label: "Approvals", icon: Pencil, adminOnly: true },
      { to: "/audit", label: "Audit Log", icon: ScrollText, module: "audit" },
    ],
  },
];

const STORAGE_KEY = "sidebar_open_groups_v2";

export default function Sidebar() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const canSee = (item) => {
    if (item.adminOnly) {
      return user?.role?.is_super_admin || user?.role?.name === "Plant Head/Admin";
    }
    const m = item.module;
    return !m || hasPermission(m, "view") || user?.role?.is_super_admin;
  };
  const isSA = !!user?.role?.is_super_admin;

  // Group open/closed state persisted to localStorage.
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return Object.fromEntries(GROUPS.filter((g) => g.title).map((g) => [g.title, !!g.defaultOpen]));
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(openGroups)); } catch { /* ignore */ }
  }, [openGroups]);
  const toggleGroup = (title) => setOpenGroups((prev) => ({ ...prev, [title]: !prev[title] }));

  // Pending delete/edit/over-receipt counters — PH/SA only.
  const [pendingDel, setPendingDel] = useState(0);
  const [pendingEdit, setPendingEdit] = useState(0);
  const [pendingOver, setPendingOver] = useState(0);
  const isPHorSA = isSA || user?.role?.name === "Plant Head/Admin";
  useEffect(() => {
    if (!isPHorSA) return;
    let alive = true;
    const tick = async () => {
      try {
        const [r, or] = await Promise.all([
          api.get("/notifications/counts"),
          api.get("/notifications/counts/over-receipt").catch(() => ({ data: {} })),
        ]);
        if (alive) {
          setPendingDel(r.data?.pending_delete_requests || 0);
          setPendingEdit(r.data?.pending_edit_requests || 0);
          setPendingOver(or.data?.pending_over_receipt || 0);
        }
      } catch { /* silent */ }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [isPHorSA]);
  const pendingTotal = pendingDel + pendingEdit + pendingOver;

  const renderItem = (item) => {
    if (!canSee(item)) return null;
    const Icon = item.icon;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === "/"}
        data-testid={`nav-${item.module || item.to.replace(/^\//, "")}`}
        className={({ isActive }) =>
          `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-4 ${
            isActive
              ? "border-l-[#2E7D32] bg-white/5 text-white"
              : "border-l-transparent text-white/80 hover:bg-white/5 hover:text-white"
          }`
        }
      >
        <Icon size={16} strokeWidth={2} />
        <span>{item.label}</span>
      </NavLink>
    );
  };

  return (
    <aside className="w-64 bg-[#0D2B5E] text-white flex flex-col h-screen sticky top-0 no-print" data-testid="sidebar">
      <div className="px-4 pt-5 pb-4 border-b border-white/10">
        <div className="bg-white rounded-sm p-2 flex items-center justify-center">
          <img src={LOGO_URL} alt="Supplar" className="h-12 object-contain"/>
        </div>
        <div className="text-[9px] tracking-[0.3em] text-white/50 uppercase text-center mt-2">Inventory Control</div>
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        {GROUPS.map((g, i) => {
          if (!g.title) {
            // Untitled (top-level) — always visible
            return <div key={`g-${i}`}>{g.items.map(renderItem)}</div>;
          }
          // Hide entire group if user can't see any item under it
          const visibleItems = g.items.filter(canSee);
          if (visibleItems.length === 0) return null;
          const isOpen = openGroups[g.title] ?? !!g.defaultOpen;
          return (
            <div key={`g-${i}`}>
              <button
                onClick={() => toggleGroup(g.title)}
                data-testid={`sidebar-group-${g.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="w-full flex items-center justify-between px-5 pt-5 pb-1 text-[10px] tracking-[0.2em] text-white/40 font-bold hover:text-white/70 transition-colors">
                <span>{g.title}</span>
                {isOpen ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
              </button>
              {isOpen && <div>{visibleItems.map(renderItem)}</div>}
            </div>
          );
        })}
      </nav>

      <div className="px-4 py-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-[#1565C0] rounded-full flex items-center justify-center text-xs font-bold">
            {user?.name?.slice(0, 2).toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[10px] tracking-wider text-white/50 uppercase truncate">
              {user?.designation || user?.role?.name}
            </div>
          </div>
          <button onClick={() => navigate(pendingTotal > 0 ? "/approvals" : "/alerts")}
            className="relative text-white/60 hover:text-white" data-testid="nav-alerts" title={pendingTotal > 0 ? `${pendingDel} delete · ${pendingEdit} edit · ${pendingOver} over-receipt pending` : "Alerts"}>
            <Bell size={16} />
            {pendingTotal > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-[#C62828] text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center" data-testid="bell-badge-count">
                {pendingTotal > 9 ? "9+" : pendingTotal}
              </span>
            )}
          </button>
        </div>
        <button onClick={async () => { await logout(); navigate("/login"); }}
          className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-[#C62828] text-white text-xs tracking-wider uppercase py-2 rounded-sm transition-colors"
          data-testid="logout-btn">
          <LogOut size={14} /> Logout
        </button>
      </div>
    </aside>
  );
}
